import { useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getEvalOptionsOptions, listEvalFixturesOptions, listEvalRunsOptions } from '@/api/generated/@tanstack/react-query.gen';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { elapsed, LiveRunTile } from '@/components/Evals/LiveRunTile';
import { pct, scoreTone } from '@/components/Evals/format';
import { RunsTable } from '@/components/Evals/RunsTable';
import { cn } from '@/lib/utils';
import { useSetAtom } from 'jotai';
import { NewExperimentDialog, newExperimentOpenAtom } from '@/components/Evals/NewExperimentDialog';

type RunsSearch = { status: string; fixture?: string; persona?: string; page: number };

export const Route = createFileRoute('/_auth/evals/runs/')({
  validateSearch: (s: Record<string, unknown>): RunsSearch => ({
    status: typeof s.status === 'string' ? s.status : 'live',
    fixture: typeof s.fixture === 'string' ? s.fixture : undefined,
    persona: typeof s.persona === 'string' ? s.persona : undefined,
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: RunsPage,
});

const TABS = [
  { id: 'live', label: 'Live' },
  { id: 'done', label: 'Done' },
  { id: 'invalid', label: 'Invalid' },
  { id: 'failed', label: 'Failed' },
  { id: '', label: 'All' },
];
const PAGE_SIZE = 24;
const ALL = '__all__';

function RunsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/evals/runs' });
  const live = search.status === 'live';
  const query = {
    status: search.status,
    fixture_id: search.fixture ?? '',
    persona: search.persona ?? '',
    page: search.page,
    page_size: PAGE_SIZE,
  };
  const runs = useQuery({
    ...listEvalRunsOptions({ query }),
    placeholderData: keepPreviousData,
    refetchInterval: live ? 3000 : 15000,
  });
  const finished = useQuery({
    ...listEvalRunsOptions({ query: { status: 'done', page: 1, page_size: 6, fixture_id: '', persona: '' } }),
    refetchInterval: 5000,
    enabled: live,
  });
  const fixtures = useQuery(listEvalFixturesOptions());
  const options = useQuery(getEvalOptionsOptions());
  const openNewExperiment = useSetAtom(newExperimentOpenAtom);
  const set = (patch: Partial<RunsSearch>) => navigate({ search: (prev) => ({ ...prev, page: 1, ...patch }) });

  // Runs that finished since the page opened get a highlight.
  const seen = useRef<Set<string> | null>(null);
  const firstLoad = seen.current === null;
  if (finished.data && firstLoad) seen.current = new Set(finished.data.items.map((r) => r.id));
  const counts = runs.data?.counts ?? {};
  const total = runs.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <EvalShell
      title="Runs"
      subtitle="Live and finished sessions."
      actions={
        <div className="flex items-center gap-2">
          <NewExperimentDialog />
          <Select value={search.fixture ?? ALL} onValueChange={(v) => set({ fixture: v === ALL ? undefined : v })}>
            <SelectTrigger className="h-9 w-48 rounded-full text-[13px]">
              <SelectValue placeholder="All sessions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All sessions</SelectItem>
              {(fixtures.data ?? []).map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={search.persona ?? ALL} onValueChange={(v) => set({ persona: v === ALL ? undefined : v })}>
            <SelectTrigger className="h-9 w-40 rounded-full text-[13px]">
              <SelectValue placeholder="All learners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All learners</SelectItem>
              {(options.data?.personas ?? []).map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
    >
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => set({ status: tab.id })}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium',
              search.status === tab.id ? 'bg-[#AAE73D] text-neutral-900' : 'bg-white text-neutral-600 ring-1 ring-black/5 hover:bg-neutral-50',
            )}
          >
            {tab.id === 'live' && (counts.live ?? 0) > 0 && <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-600" />}
            {tab.label}
            <span className="text-[11px] opacity-60">
              {tab.id ? counts[tab.id] ?? 0 : Object.entries(counts).filter(([k]) => k !== 'live').reduce((a, [, n]) => a + n, 0)}
            </span>
          </button>
        ))}
      </div>

      {!runs.data ? (
        <Skeleton className="h-72 rounded-2xl" />
      ) : live ? (
        <div className="grid gap-4 xl:grid-cols-[1fr_300px]">
          <div>
            {runs.data.items.length ? (
              <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
                {runs.data.items.map((run) => (
                  <LiveRunTile key={run.id} run={run} />
                ))}
              </div>
            ) : (
              <Panel>
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <p className="text-[15px] font-semibold text-neutral-900">Nothing running</p>
                  <p className="max-w-sm text-[13px] text-neutral-500">
                    Queue an experiment and each session shows up here as it plays, with its voice, screen and progress.
                  </p>
                  <button type="button" onClick={() => openNewExperiment(true)} className="btn-3d mt-1 h-9 rounded-full px-5 text-[13px]">
                    New experiment
                  </button>
                </div>
              </Panel>
            )}
          </div>
          <Panel title="Just finished">
            <div className="space-y-2">
              {(finished.data?.items ?? []).map((run) => (
                <Link
                  key={run.id}
                  to="/evals/experiments/$experimentId"
                  params={{ experimentId: run.experiment_id }}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-xl px-2 py-1.5 transition-colors duration-1000 hover:bg-neutral-50',
                    !firstLoad && !seen.current?.has(run.id) && 'animate-in fade-in bg-[#AAE73D]/20',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium text-neutral-800">
                      {run.fixture_key} · {run.persona}
                    </span>
                    <span className="text-[11px] text-neutral-400">{elapsed(run.finished_at)} ago</span>
                  </span>
                  <span className={cn('rounded-full px-2 py-0.5 text-[12px] font-semibold', scoreTone(run.score))}>{pct(run.score)}</span>
                </Link>
              ))}
              {!finished.data?.items.length && <p className="text-[12px] text-neutral-400">No finished runs yet.</p>}
            </div>
          </Panel>
        </div>
      ) : (
        <Panel>
          <RunsTable runs={runs.data.items} />
          <div className="mt-3 flex items-center justify-between text-[12px] text-neutral-500">
            <span>
              {total ? `${(search.page - 1) * PAGE_SIZE + 1}–${Math.min(total, search.page * PAGE_SIZE)} of ${total}` : 'No runs'}
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                className="h-8 rounded-full px-3 text-[12px]"
                disabled={search.page <= 1}
                onClick={() => navigate({ search: (prev) => ({ ...prev, page: prev.page - 1 }) })}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                className="h-8 rounded-full px-3 text-[12px]"
                disabled={search.page >= pages}
                onClick={() => navigate({ search: (prev) => ({ ...prev, page: prev.page + 1 }) })}
              >
                Next
              </Button>
            </div>
          </div>
        </Panel>
      )}
    </EvalShell>
  );
}
